import { Currency, Language, ServiceType } from "@/data/model/Enums";
import type { ILinkableEntity, IMultiLanguageText } from "@/data/model/Shared";

import { toMoneyWithSign } from "@/shared/utils/format";

export class Format {
    static serviceIcon(s: ServiceType) {
        switch (s) {
            case ServiceType.Covid:
                return "fal fa-shield-virus";
            case ServiceType.Documents:
                return "fal fa-file-alt";
            case ServiceType.Order:
                return "fal fa-shopping-cart";
            case ServiceType.Ticketing:
                return "fal fa-ticket-alt";
            case ServiceType.Website:
                return "fal fa-external-link";
            case ServiceType.Offer:
                return "fal fa-clipboard-list";
            case ServiceType.Pay:
                return "fal fa-money-bill-alt";
            case ServiceType.RoomPlan:
                return "fal fa-border-style-alt";
        }
        return "";
    }

    static cardIconByBrand(brand: string | undefined): string | undefined {
        if (brand === undefined) return undefined;

        switch (brand.toLowerCase()) {
            case "visa":
                return "/assets/img/brands/cc_visa.svg";
            case "mastercard":
                return "/assets/img/brands/cc_mastercard.svg";
            case "amex":
                return "/assets/img/brands/cc_amex.svg";
            case "jcb":
                return "/assets/img/brands/cc_jcb.svg";
            case "unionpay":
                return "/assets/img/brands/cc_unionpay.svg";
            case "diners":
                return "/assets/img/brands/cc_diners.svg";
            case "lunchcheck":
                return "/assets/img/brands/lunchcheck.svg";
            case "postfinance":
                return "/assets/img/brands/postfinance.svg";
            case "mastercardmaestro":
                return "/assets/img/brands/maestro.svg";
            case "visavpay":
                return "/assets/img/brands/visa-vpay.svg";
            case "visaelectron":
                return "/assets/img/brands/visa-electron.svg";
            case "visapaywave":
                return "/assets/img/brands/visa-paywave.svg";
            case "unknown":
                return undefined;
        }

        return undefined;
    }

    static translateProperty<T>(obj: any, prop: string, lang: Language): T {
        //check for prefered language
        const pProp = prop + Format.languageShort(lang);
        const pResolved = obj[pProp];
        if (pResolved !== undefined && pResolved != null) return pResolved;

        //language entry does not exist. return default value
        const checkables = [Language.English, Language.German, Language.French, Language.Italian];
        let dResolved: any;

        for (const ai in checkables) {
            const l = checkables[ai];
            if (l === lang) continue;

            const dProp = prop + Format.languageShort(l);
            const f = obj[dProp];
            if (f !== undefined && f != null) {
                dResolved = f;
                break;
            }
        }

        return dResolved;
    }

    static translate(val: IMultiLanguageText | undefined, lang: Language, returnInExactLanguage: boolean = false) {
        if (val === undefined) return "";

        const pointer = Format.languageShort(lang).toLowerCase();
        const resolved = val[pointer];
        if (returnInExactLanguage || (resolved !== undefined && resolved != null)) return resolved;

        const checkables = [Language.English, Language.German, Language.French, Language.Italian];
        let dResolved: any;

        for (const ai in checkables) {
            const l = checkables[ai];
            if (l === lang) continue;

            const pointer2 = Format.languageShort(l).toLowerCase();
            const f = val[pointer2];
            if (f !== undefined && f != null) {
                dResolved = f;
                break;
            }
        }

        return dResolved;
    }

    static languageShort(lang: Language) {
        switch (lang) {
            case Language.German:
                return "De";
            case Language.English:
                return "En";
            case Language.French:
                return "Fr";
            case Language.Italian:
                return "It";
        }

        return "";
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    static toMoneyFormat(price: number, currency?: Currency): string {
        return toMoneyWithSign(price, true);
    }

    static absoluteUrl(url: string): string {
        if (url.indexOf("http://") === 0 || url.indexOf("https://") === 0) return url;

        return "http://" + url;
    }

    static toSimpleCommaSeparatedList<T>(entries: Array<T>, nameProperty: { (m: T): string }) {
        let str = "";
        for (const i in entries) {
            const prop = nameProperty(entries[i]);
            str += prop + ", ";
        }

        if (str.length > 2) str = str.substring(0, str.length - 2);

        return str;
    }

    static linkableEntityName(searchResult: ILinkableEntity): string | undefined {
        let name = Format.htmlEscape(searchResult.name);

        if (searchResult.appearsIn !== undefined && searchResult.appearsIn.length !== 0) {
            name += '<span class="appearsin">(';

            for (const i in searchResult.appearsIn) name += Format.htmlEscape(searchResult.appearsIn[i].name) + ", ";

            name = name!.substring(0, name!.length - 2) + ")</span>";
        }

        return name;
    }

    static htmlEscape(inpt?: string): string | undefined {
        if (inpt === undefined) return undefined;

        const matchHtmlRegExp = /["'&<>]/;
        const str = "" + inpt;
        const match = matchHtmlRegExp.exec(str);

        if (!match) {
            return str;
        }

        let escape;
        let html = "";
        let index = 0;
        let lastIndex = 0;

        for (index = match.index; index < str.length; index++) {
            switch (str.charCodeAt(index)) {
                case 34: // "
                    escape = "&quot;";
                    break;
                case 38: // &
                    escape = "&amp;";
                    break;
                case 39: // '
                    escape = "&#39;";
                    break;
                case 60: // <
                    escape = "&lt;";
                    break;
                case 62: // >
                    escape = "&gt;";
                    break;
                default:
                    continue;
            }

            if (lastIndex !== index) {
                html += str.substring(lastIndex, index);
            }

            lastIndex = index + 1;
            html += escape;
        }

        return lastIndex !== index ? html + str.substring(lastIndex, index) : html;
    }

    static endWithFullstop(txt: string): string {
        if (txt === undefined || txt == null) return txt;

        if (!~[".", "!", "?", ";"].indexOf(txt[txt.length - 1])) txt += ".";

        return txt;
    }
}
